import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { languages, type LanguageCode } from "@/i18n";
import { cn } from "@/lib/utils";
import { nonEnglishLanguages, rtlLanguages } from "@/lib/seo";

interface LanguageSelectorProps {
  variant?: "default" | "compact";
  isScrolled?: boolean;
}

export function LanguageSelector({
  variant = "default",
  isScrolled = false,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();

  const currentLang =
    languages.find((l) => l.code === i18n.language) || languages[0];

  const handleLanguageChange = (newLangCode: LanguageCode) => {
    // Update i18n
    i18n.changeLanguage(newLangCode);
    localStorage.setItem("vx-language", newLangCode);

    // Update document direction for RTL languages
    document.documentElement.dir = rtlLanguages.has(newLangCode) ? "rtl" : "ltr";
    document.documentElement.lang = newLangCode;

    // Get current path without any language prefix
    let basePath = location.pathname;

    // Remove existing lang prefix if present
    if (lang && nonEnglishLanguages.has(lang)) {
      basePath = basePath.replace(`/${lang}`, "") || "/";
    }

    // Build new path
    let newPath: string;
    if (newLangCode === "en") {
      // English: serve from root (no prefix)
      newPath = basePath + location.search + location.hash;
    } else {
      // Other languages: add prefix
      newPath = `/${newLangCode}${basePath === "/" ? "" : basePath}${location.search}${location.hash}`;
    }

    navigate(newPath);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-9 gap-1.5 font-medium transition-colors",
            variant === "compact" && "px-2.5",
            "text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/15"
          )}
        >
          <Globe className="h-4 w-4" />
          <span className="text-sm font-medium">
            {currentLang.code.toUpperCase()}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-52 max-h-[400px] overflow-y-auto bg-popover border border-border shadow-lg z-50"
      >
        {languages.map((langItem) => (
          <DropdownMenuItem
            key={langItem.code}
            onClick={() => handleLanguageChange(langItem.code)}
            className={cn(
              "flex items-center justify-between cursor-pointer py-2.5 px-3",
              i18n.language === langItem.code && "bg-muted"
            )}
          >
            <span className="font-medium">{langItem.nativeName}</span>
            <span className="text-xs text-muted-foreground">
              {langItem.name}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
